import { createContext, useContext, useMemo, useReducer } from "react"
import { useDeleteTemplate, useSaveTemplate } from "resources/templates"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"

export const AssetViewContext = createContext()

const PREFERENCE_SUBTYPE = "asset-view"

const DEFAULT_STATE = {
  view: "performance",
  groupBy: null,
  selectedTableLevels: [],
  showMetricsSelector: false,
  columnOrder: "default",
  datagridState: {},
}

// Define which fields should be saved as preferences
const PREFERENCE_FIELDS = [
  "view",
  "groupBy",
  "selectedTableLevels",
  "showNotHeld",
  "columnOrder",
  "datagridState",
]

function reducer(current, action) {
  return produce(current, draft => {
    switch (action.type) {
      case "setView":
        draft.view = action.view
        break
      case "setExpanded":
        draft.isExpanded = action.isExpanded
        break
      case "setGroupBy":
        draft.groupBy = action.groupBy
        // Reset selectedTableLevels when groupBy changes since the previous path may no longer be valid
        draft.selectedTableLevels = []
        break
      case "setShowNotHeld":
        draft.showNotHeld = action.showNotHeld
        break
      case "setShowMetricsSelector":
        draft.showMetricsSelector = action.showMetricsSelector
        break
      case "setSelectedTableLevels":
        draft.selectedTableLevels = action.selectedTableLevels
        break
      case "setColumnOrder":
        draft.columnOrder = action.columnOrder
        break
      case "setDatagridState":
        draft.datagridState = action.datagridState
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
    }
  })
}

function setView(view) {
  return {
    type: "setView",
    view,
  }
}

function setGroupBy(groupBy) {
  return {
    type: "setGroupBy",
    groupBy,
  }
}

function setSelectedTableLevels(selectedTableLevels) {
  return {
    type: "setSelectedTableLevels",
    selectedTableLevels,
  }
}

function setShowNotHeld(showNotHeld) {
  return {
    type: "setShowNotHeld",
    showNotHeld,
  }
}

function setShowMetricsSelector(showMetricsSelector) {
  return {
    type: "setShowMetricsSelector",
    showMetricsSelector,
  }
}

function setColumnOrder(columnOrder) {
  return {
    type: "setColumnOrder",
    columnOrder,
  }
}

function setDatagridState(datagridState) {
  return {
    type: "setDatagridState",
    datagridState,
  }
}

function setPreferences(preferences) {
  return {
    type: "setPreferences",
    preferences,
  }
}

export default function useAssetViewContext(parentContext) {
  const parentContextValue = useContext(parentContext)
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
  )

  const contextUpdaters = useMemo(() => ({
    setView: (...args) => dispatch(setView(...args)),
    setGroupBy: (...args) => dispatch(setGroupBy(...args)),
    setShowNotHeld: (...args) => dispatch(setShowNotHeld(...args)),
    setShowMetricsSelector: (...args) => dispatch(setShowMetricsSelector(...args)),
    setSelectedTableLevels: (...args) => dispatch(setSelectedTableLevels(...args)),
    setColumnOrder: (...args) => dispatch(setColumnOrder(...args)),
    setDatagridState: (...args) => dispatch(setDatagridState(...args)),
  }), [dispatch])

  const deleteTemplate = useDeleteTemplate()
  const saveTemplate = useSaveTemplate()

  const contextValue = useMemo(() => ({
    ...parentContextValue,
    ...state,
    ...contextUpdaters,
    deleteTemplate,
    saveTemplate,
    isPreferencesLoaded,
  }), [state, parentContextValue, contextUpdaters, deleteTemplate, saveTemplate, isPreferencesLoaded])

  return [AssetViewContext, contextValue]
}
